import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { MiddlewareBaseResponse } from '@ireckonu/shared-ui';

import { APP_ENV } from '../app-config/app-config.token';
import {
  ConfigurationApiResponse,
  ConfigurationServicesResponse,
  FeatureTogglesApiInterface,
} from '../models';

@Injectable({ providedIn: 'root' })
export class ConfigurationQueryService {
  private readonly http = inject(HttpClient);
  private readonly configApiBaseUrl =
    inject(APP_ENV).configApiBaseUrl + '/api/v1';

  private readonly services = signal<ConfigurationServicesResponse | null>(
    null,
  );
  readonly features = signal<FeatureTogglesApiInterface | null>(null);
  readonly dateFormat = signal<string | undefined>(undefined);
  readonly customerName = signal<string | null>(null);
  private configuration$: Observable<FeatureTogglesApiInterface> | null = null;

  loadConfiguration(): Observable<FeatureTogglesApiInterface> {
    this.configuration$ ??= this.http
      .get<
        MiddlewareBaseResponse<ConfigurationApiResponse>
      >(`${this.configApiBaseUrl}/configuration`)
      .pipe(
        map((response) => {
          if (!response.Success) {
            const errorMessage =
              response.Errors?.[0]?.Message ?? 'Configuration load failed';
            throw new Error(errorMessage);
          }

          return {
            features: {
              loyalty: response.Data.Features.Loyalty,
              venues: response.Data.Features.Venues,
              preferences: response.Data.Features.Preferences,
              rfm: response.Data.Features.Rfm,
              katakanaKanjiName: response.Data.Features.KatakanaKanjiName,
              profileDosAndDonts: response.Data.Features.ProfileDosAndDonts,
              profileFreeTextPreference:
                response.Data.Features.ProfileFreeTextPreference,
              showDateOfBirth: !response.Data.Features.HideDateOfBirth,

              personIdLabel: response.Data?.UiConfig?.PersonIdLabel,
              rfmTierLabel: response.Data?.UiConfig?.RfmTier,
            } satisfies FeatureTogglesApiInterface,
            services: response.Data.Services,
            dateFormat: response.Data.DateFormat,
            customerName: response.Data.Customer?.DisplayName ?? null,
          };
        }),
        tap(({ features, services, dateFormat, customerName }) => {
          this.features.set(features);
          this.services.set(services);
          this.customerName.set(customerName);
          if (dateFormat) {
            this.dateFormat.set(dateFormat);
          }
        }),
        map(({ features }) => features),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.configuration$;
  }

  getServiceUrl<K extends keyof ConfigurationServicesResponse>(
    key: K,
  ): ConfigurationServicesResponse[K] | undefined {
    return this.services()?.[key];
  }
}
